import { FormEvent, useEffect, useMemo, useState } from "react";
import { createAdminContract, deleteAdminContract, getAdminContracts, type AdminContractItem } from "../api/adminContractsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import Swal from "sweetalert2";

const AUDIENCE_OPTIONS = [
  { label: "Tüm üyeler", value: 1, description: "Platformdaki tüm kayıtlı kullanıcılar" },
  { label: "Creator üyeler", value: 2, description: "Çizer / Creator rolü olan kullanıcılar" },
];

const FORM_OPTIONS = [
  { label: "Üyelik kaydı", value: 1, description: "register formunda göster" },
  { label: "Creator başvurusu", value: 2, description: "creator submit / panel geçişinde göster" },
  { label: "Creator ödeme", value: 4, description: "IBAN ve ödeme ayarları ekranında göster" },
];

function toggleFlag(current: number, flag: number, enabled: boolean) {
  if (enabled) {
    return current | flag;
  }
  return current & ~flag;
}

function hasFlag(current: number, flag: number) {
  return (current & flag) === flag;
}

export default function AdminContracts() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Contracts.View");
  const { allowed: canManage } = usePermissionCheck("Contracts.Manage");
  const [contracts, setContracts] = useState<AdminContractItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    slug: "kvkk",
    title: "",
    version: "1.0",
    bodyHtml: "",
    isMandatory: true,
    isPublished: true,
    effectiveFrom: new Date().toISOString().slice(0, 16),
    audienceTargets: 1,
    formTargets: 1,
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const list = await getAdminContracts();
      if (active) {
        setContracts(list);
        setLoading(false);
      }
    };
    if (allowed) {
      load();
    } else {
      setLoading(false);
    }
    return () => {
      active = false;
    };
  }, [allowed]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManage) {
      Swal.fire("Yetki yok", "Sözleşme ekleme yetkin bulunmuyor.", "warning");
      return;
    }

    const payload = {
      ...form,
      slug: form.slug.trim().toLowerCase(),
      title: form.title.trim() || "Sözleşme",
      version: form.version.trim(),
      bodyHtml: form.bodyHtml.trim(),
      effectiveFrom:form.effectiveFrom ? new Date(form.effectiveFrom).toISOString() : new Date().toISOString(),
      audienceTargets: form.audienceTargets || 1,
      formTargets: form.formTargets || 1,
    };

    const res = await createAdminContract(payload);
    if (res.success) {
      Swal.fire("Kaydedildi", `${payload.slug} v${payload.version} oluşturuldu.`, "success");
      setForm((prev) => ({ ...prev, version: (parseFloat(prev.version) + 0.1).toFixed(1) }));
      const list = await getAdminContracts();
      setContracts(list);
    } else {
      Swal.fire("Hata", "Sözleşme kaydedilemedi.", "error");
    }
  };

  const handleDelete = async (id: string, slug: string, version: string) => {
    if (!canManage) {
      Swal.fire("Yetki yok", "Sözleşmeleri silme yetkin bulunmuyor.", "warning");
      return;
    }

    const confirmation = await Swal.fire({
      title: `${slug} v${version} silinsin mi?`,
      text: "Bu sözleşme arşivlenecek ve yeni kayıtlarda görünmeyecek.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sil",
      cancelButtonText: "İptal",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    const ok = await deleteAdminContract(id);
    if (!ok) {
      Swal.fire("Hata", "Sözleşme silinemedi.", "error");
      return;
    }

    Swal.fire("Silindi", `${slug} (${version}) arşivlendi.`, "success");
    const list = await getAdminContracts();
    setContracts(list);
  };

  const formAudienceTags = useMemo(
    () => renderTagList(form.audienceTargets, AUDIENCE_OPTIONS),
    [form.audienceTargets],
  );
  const formTargetTags = useMemo(() => renderTagList(form.formTargets, FORM_OPTIONS), [form.formTargets]);

  if (checkingPermission) {
    return <div className="rounded bg-white p-6 text-center text-sm text-slate-500">Yetkiler doğrulanıyor...</div>;
  }

  if (!allowed) {
    return <div className="rounded bg-white p-6 text-center text-sm text-red-500">Bu sayfaya erişim yetkin yok.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl bg-white p-6 shadow">
        <h1 className="text-xl font-semibold text-slate-900">Sözleşmeler</h1>
        <p className="text-sm text-slate-500">KVKK, üyelik ve creator sözleşmelerini hedef kitlesine göre yönet.</p>
      </header>

      <section className="rounded-3xl bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-slate-900">Yeni versiyon</h2>
        <form onSubmit={handleSubmit} className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm text-slate-600">
            Slug
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="kvkk, membership_terms"
              required
            />
          </label>
          <label className="text-sm text-slate-600">
            Versiyon
            <input
              type="text"
              value={form.version}
              onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>
          <label className="text-sm text-slate-600 md:col-span-2">
            Başlık
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="KVKK Aydınlatma Metni"
              required
            />
          </label>
          <label className="text-sm text-slate-600 md:col-span-2">
            İçerik (HTML destekli)
            <textarea
              value={form.bodyHtml}
              onChange={(e) => setForm((prev) => ({ ...prev, bodyHtml: e.target.value }))}
              className="mt-1 h-40 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="<p>Metin...</p>"
              required
            />
          </label>
          <label className="text-sm text-slate-600">
            Yürürlük Tarihi
            <input
              type="datetime-local"
              value={form.effectiveFrom}
              onChange={(e) => setForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>
          <div className="flex flex-col gap-2 text-sm text-slate-600">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isMandatory}
                onChange={(e) => setForm((prev) => ({ ...prev, isMandatory: e.target.checked }))}
              />
              Zorunlu
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
              />
              Yayında
            </label>
            <div className="rounded-2xl border border-slate-200/80 p-3 text-xs text-slate-500">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Hedef kitle</div>
              <div className="mt-2 space-y-1">
                {AUDIENCE_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center justify-between gap-2">
                    <span>
                      {option.label}
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">{option.description}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={hasFlag(form.audienceTargets, option.value)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          audienceTargets: toggleFlag(prev.audienceTargets, option.value, e.target.checked),
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-emerald-500">{formAudienceTags || "Seçilmedi"}</div>
            </div>
            <div className="rounded-2xl border border-slate-200/80 p-3 text-xs text-slate-500">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Formlar</div>
              <div className="mt-2 space-y-1">
                {FORM_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center justify-between gap-2">
                    <span>
                      {option.label}
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">{option.description}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={hasFlag(form.formTargets, option.value)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          formTargets: toggleFlag(prev.formTargets, option.value, e.target.checked),
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-brand-500">{formTargetTags || "Seçilmedi"}</div>
            </div>
            <button
              type="submit"
              className={`mt-2 rounded px-4 py-2 text-sm font-semibold text-white ${
                canManage ? "bg-slate-900 hover:bg-slate-700" : "cursor-not-allowed bg-slate-500"
              }`}
              disabled={!canManage}
            >
              Kaydet
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-3xl bg-white p-6 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Versiyonlar</h2>
          <span className="text-xs text-slate-400">Toplam {contracts.length}</span>
        </div>
        {loading ? (
          <div className="mt-4 text-sm text-slate-500">Yükleniyor...</div>
        ) : contracts.length === 0 ? (
          <div className="mt-4 text-sm text-slate-500">Henüz sözleşme yok.</div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase text-slate-500">
                  <th className="px-2 py-1">Slug</th>
                  <th className="px-2 py-1">Versiyon</th>
                  <th className="px-2 py-1">Başlık</th>
                  <th className="px-2 py-1">Hedef kitle</th>
                  <th className="px-2 py-1">Formlar</th>
                  <th className="px-2 py-1">Zorunlu</th>
                  <th className="px-2 py-1">Yayında</th>
                  <th className="px-2 py-1">Yürürlük</th>
                  {canManage && <th className="px-2 py-1 text-right">Aksiyon</th>}
                </tr>
              </thead>
              <tbody>
                {contracts.map((item) => (
                  <tr key={item.id} className="border-b last:border-none">
                    <td className="px-2 py-2 text-xs font-semibold text-slate-700">{item.slug}</td>
                    <td className="px-2 py-2 text-xs text-slate-500">{item.version}</td>
                    <td className="px-2 py-2">{item.title}</td>
                    <td className="px-2 py-2 text-[11px] text-slate-500">{renderTagList(item.audienceTargets, AUDIENCE_OPTIONS) || "—"}</td>
                    <td className="px-2 py-2 text-[11px] text-slate-500">{renderTagList(item.formTargets, FORM_OPTIONS) || "—"}</td>
                    <td className="px-2 py-2 text-xs">{item.isMandatory ? "Evet" : "Hayır"}</td>
                    <td className="px-2 py-2 text-xs">{item.isPublished ? "Yayında" : "Taslak"}</td>
                    <td className="px-2 py-2 text-xs text-slate-500">{new Date(item.effectiveFrom).toLocaleString("tr-TR")}</td>
                    {canManage && (
                      <td className="px-2 py-2 text-right text-xs">
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id, item.slug, item.version)}
                          className="rounded-full border border-red-200 px-3 py-1 text-red-500 hover:bg-red-50"
                        >
                          Sil
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function renderTagList(flags: number, options: Array<{ value: number; label: string }>): string {
  if (!flags) {
    return "";
  }
  const values = options.filter((option) => hasFlag(flags, option.value)).map((option) => option.label);
  return values.join(", ");
}
